import type { NhostClient } from '@nhost/react';

export type EmailChangeConfirmationResult =
  | { status: 'confirmed'; email: string }
  | { status: 'invalid' | 'unavailable' };

type ConfirmationAuth = Pick<NhostClient['auth'], 'refreshSession'>;

// Capture and remove callback credentials before Nhost or analytics can consume the URL.
export function prepareEmailChangeConfirmation():
  | ((auth: ConfirmationAuth) => Promise<EmailChangeConfirmationResult>)
  | null {
  const url = new URL(window.location.href);
  const fragment = new URLSearchParams(url.hash.slice(1));
  const values = (key: string) => [
    ...url.searchParams.getAll(key),
    ...fragment.getAll(key),
  ];
  const types = values('type');
  const tokens = values('refreshToken');
  const isEmailChange = types.length === 1 && types[0] === 'emailConfirmChange';
  const isLegacyCallback =
    url.pathname === '/' && isEmailChange && tokens.length === 1 && !!tokens[0];

  if (
    url.pathname.replace(/\/$/, '') !== '/email-change' &&
    !isLegacyCallback
  ) {
    return null;
  }

  const hasError = ['error', 'errorDescription', 'error_description'].some(
    (key) => values(key).length > 0,
  );
  let token =
    isEmailChange && !hasError && tokens.length === 1 ? tokens[0] : undefined;
  window.history.replaceState(window.history.state, '', '/email-change');
  let pending: Promise<EmailChangeConfirmationResult> | undefined;

  return (auth) => {
    if (!pending) {
      const callbackToken = token;
      token = undefined;
      pending = new Promise((resolve) => {
        if (!callbackToken) {
          resolve({ status: 'invalid' });
          return;
        }
        // The SDK request cannot be aborted. Stop waiting without retrying the link.
        const timeout = setTimeout(() => resolve({ status: 'unavailable' }), 20_000);
        (async (): Promise<EmailChangeConfirmationResult> => {
          try {
            // Validate this link's session explicitly. An existing signed-in user is not proof.
            const { session, error } = await auth.refreshSession(callbackToken);
            if (error || !session?.user.emailVerified || !session.user.email) {
              return { status: 'invalid' };
            }
            return { status: 'confirmed', email: session.user.email };
          } catch {
            return { status: 'unavailable' };
          }
        })().then((result) => {
          clearTimeout(timeout);
          resolve(result);
        });
      });
    }
    return pending;
  };
}
